import { useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import pallet from '../../layouts/pallet';
import {
  AddUserButtonWrapper,
  HeaderBar,
  LoadingOverlay
} from '../AdminUsers/styled';
import { Button, CircularProgress } from '@mui/material';
import hashtagsApi from '../../apis/hashtags';
import ToastMessage from '../../components/ToastMessage';
import { SnackbarSeverityType } from '../../enums';
import { columns, parseValidationErrors, schema } from './utils/utils';
import AddHashtagDialog from './AddHashtagsDialog';
import { useHashtags } from './utils/useHashtags';
import { dataGridSx } from '../common/styled/DataGrid';
import Icons from '../../components/icons';
import tableColumnUtils from '../../utils/tableColumnUtils';
import { PageWrapper, Title } from '../common/styled';
import CustomDataGridToolbar from '../common/components/CustomDataGridToolbar';

const HashtagsPage = () => {
  const [openAdd, setOpenAdd] = useState(false);
  const [creatingHashtag, setCreatingHashtag] = useState(false);
  const [hashtag, setHashtag] = useState('');
  const [errors, setErrors] = useState<{ name?: string }>({});
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: SnackbarSeverityType.success
  });

  const showToast = (message: string, severity: SnackbarSeverityType) =>
    setToast({ open: true, message, severity });

  const { rows, loading: listLoading, getHashtags } = useHashtags(showToast);

  const resetDialog = () => {
    setHashtag('');
    setErrors({});
  };

  const handleClose = () => {
    setOpenAdd(false);
    resetDialog();
  };

  const handleCreateHashtag = async () => {
    try {
      setCreatingHashtag(true);
      await schema.validate({ name: hashtag }, { abortEarly: false });
      setErrors({});
      await hashtagsApi.createHashtag(hashtag.trim());
      showToast('Hashtag added', SnackbarSeverityType.success);
      handleClose();
      getHashtags();
    } catch (error: any) {
      const fieldErrors = parseValidationErrors(error);
      if (Object.keys(fieldErrors).length > 0) {
        setErrors(fieldErrors);
      } else {
        showToast('Failed to add hashtag', SnackbarSeverityType.error);
      }
    } finally {
      setCreatingHashtag(false);
    }
  };

  const handleAddHashtagDialogOpen = async () => {
    setOpenAdd(true);
  };

  const handleAddHashtag = (value: string) => {
    setHashtag(value);
  };

  return (
    <>
      <HeaderBar>
        <Title variant='h4'>Hashtags</Title>
        <AddUserButtonWrapper>
          <Button variant='contained' onClick={handleAddHashtagDialogOpen}>
            <Icons.PlusWhite style={{ marginRight: '6px' }} /> Add Hashtag
          </Button>
        </AddUserButtonWrapper>
      </HeaderBar>
      <PageWrapper>
        <DataGrid
          rows={rows}
          columns={columns}
          disableRowSelectionOnClick
          showToolbar
          disableColumnSelector
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              printOptions: { disableToolbarButton: true },
              csvOptions: { disableToolbarButton: true }
            }
          }}
          pageSizeOptions={tableColumnUtils.PAGE_SIZE_OPTIONS}
          initialState={tableColumnUtils.DATA_GRID_INITIAL_STATE}
          sx={{
            ...dataGridSx(pallet),
            '& .MuiDataGrid-virtualScroller': {
              overflowY: 'auto'
            }
          }}
          loading={listLoading}
          slots={{ toolbar: CustomDataGridToolbar }}
        />
        {listLoading && (
          <LoadingOverlay>
            <CircularProgress />
          </LoadingOverlay>
        )}
      </PageWrapper>

      <AddHashtagDialog
        open={openAdd}
        loading={creatingHashtag}
        hashtag={hashtag}
        errors={errors}
        onClose={handleClose}
        onChange={handleAddHashtag}
        onSubmit={handleCreateHashtag}
      />
      <ToastMessage
        {...toast}
        onClose={() => setToast({ ...toast, open: false })}
      />
    </>
  );
};

export default HashtagsPage;
